import React, { useState, useEffect } from "react"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import Tooltip from "aldoo-ra/Tooltip"
import Button from "aldoo-ra/Button"
import { EyeIcon } from "@heroicons/react/24/outline"
import { api } from "aldoo-ra/API"
import { createRoot } from "react-dom/client"
import PreviewDialog from "aldoo-ra/HtmlEditor/plugins/page-preview/preview-dialog"

const StoreTemplateSelectorEditor = ({
  onChange,
  value,
  field,
  parentValues,
  parentObject
}) => {
  const [localValue, setLocalValue] = useState(value || "")
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value || "")
  }, [value])

  // Load available mail templates
  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    setLoading(true)
    setError(null)

    try {
      // Use CRUD API to get only system category MailTemplates
      // Model: CMSContent, Filter: content.type = 'MailTemplate' AND content.category = 'system'
      const crudApi = api('crud')
      const response = await crudApi({
        'get': {
          model: 'CMSContent',
          match: {
            'content.type': 'MailTemplate',
            'content.category': 'system',
            'deleted': { $ne: true }  // Exclude deleted templates
          },
          limit: 100,
          sort: { 'content.name': 1 }
        }
      })

      if (response.error) {
        throw new Error(response.error)
      }

      let templates = response.data || response.results || response

      if (!Array.isArray(templates)) {
        // If it's not an array but response has data, try different structures
        if (typeof response === 'object' && response !== null) {
          const possibleTemplatesArrays = [
            response.results,
            response.items,
            response.list,
            response.mailTemplates,
            response.MailTemplates
          ]

          for (const possibleArray of possibleTemplatesArrays) {
            if (Array.isArray(possibleArray)) {
              templates = possibleArray
              break
            }
          }
        }

        if (!Array.isArray(templates)) {
          setTemplates([])
          setError('No templates data found')
          return
        }
      }

      // No need to filter since CRUD API already returned only system templates
      const systemTemplates = templates

      const templateOptions = systemTemplates.map(template => ({
        value: template.id || template.content?.id || template._id,
        name: template.name || template.content?.name || template.id || 'Unnamed Template',
        description: template.description || template.content?.description || '',
        category: template.category || template.content?.category || 'system',
        template: template // Store full template object for reference
      }))

      setTemplates(templateOptions)
    } catch (err) {
      console.error('Failed to load mail templates:', err)
      setError(err.message)
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }

  const handleSelectionChange = (selectedOptions) => {
    const selectedTemplate = selectedOptions?.[0] || null
    const templateId = selectedTemplate?.value || ""

    setLocalValue(templateId)
    onChange?.(templateId)
  }

  const handlePreviewTemplate = async (template) => {
    if (!template?.value) return

    // Check if preview is already open
    const existingPreview = document.querySelector(".preview-dialog-container")
    if (existingPreview) {
      existingPreview.remove()
      return
    }

    try {
      // Fetch the full template content including HTML
      const crudApi = api('crud')
      const response = await crudApi({
        'get': {
          model: 'CMSContent',
          match: {
            'content.id': template.value,
            'content.type': 'MailTemplate'
          },
          limit: 1
        }
      })

      // Handle different response structures
      let templateData = null
      if (Array.isArray(response)) {
        templateData = response[0]
      } else if (response?.data && Array.isArray(response.data)) {
        templateData = response.data[0]
      } else if (response?.results && Array.isArray(response.results)) {
        templateData = response.results[0]
      } else if (response?.content) {
        templateData = response
      }

      if (templateData) {
        // Check different possible paths for HTML content
        const htmlContent =
          templateData.content?.html ||
          templateData.content?.source ||
          templateData.html ||
          templateData.source ||
          null

        if (htmlContent) {
          // Use the same preview system as the HTML editor
          const container = document.createElement("div")
          container.className = "preview-dialog-container"
          document.body.appendChild(container)

          const root = createRoot(container)
          root.render(
            <PreviewDialog
              content={htmlContent}
              onClose={() => {
                root.unmount()
                container.remove()
              }}
              openHtmlEditor={null} // Hide HTML editor option
            />
          )
        } else {
          console.warn('No HTML content found in template data:', templateData)
          // Navigate to template editor to view/edit HTML content
          const templateEditUrl = `/admin/mail-templates/edit/${template.value}`
          window.open(templateEditUrl, '_blank')
        }
      } else {
        console.warn('No template data found in response')
      }
    } catch (error) {
      console.error('Failed to load template content:', error)
    }
  }

  // Find selected template for display
  const selectedTemplate = templates.find(t => t.value === localValue)


  // If we have a value but no matching template, create a placeholder option
  let selectedOptions = []
  if (selectedTemplate) {
    selectedOptions = [selectedTemplate]
  } else if (localValue && localValue !== "" && !loading) {
    // Create a placeholder option for unknown template IDs
    selectedOptions = [{
      value: localValue,
      name: localValue,
      description: 'Template not found in current list',
      category: 'unknown'
    }]
  }

  const isRequired = typeof field.required === "function"
    ? field.required(parentObject)
    : field.required

  const getCategoryBadgeClass = (category) => {
    const categoryColors = {
      'system': 'bg-admin_info text-white',
      'user': 'bg-admin_success text-white',
      'store': 'bg-admin_warning text-white',
      'unknown': 'bg-admin_error text-white'
    }
    return categoryColors[category] || 'bg-admin_neutral_3 text-white'
  }

  return (
    <div className="store-template-selector w-full">
      <div className="flex flex-col space-y-2">
        {error ? (
          <div className="p-4 border border-admin_error/30 bg-admin_error/10 rounded-lg">
            <Typography 
              variant="admin_body" 
              className="text-admin_error"
            >
              Error loading templates: {error}
            </Typography>
          </div>
        ) : (
          <DropdownSelect
            variant="admin"
            options={templates}
            value={localValue === "" ? [] : selectedOptions}
            onChange={handleSelectionChange}
            multiSelection={false}
            searchEnabled={true}
            showCheckbox={false}
            showSelectionTick={true}
            placeholder={loading ? "Loading system templates..." : "Please select a template"}
            disabled={loading}
            searchPlaceholder="Search system templates by name, ID, or description..."
            onItemRender={(template) => (
              <Tooltip
                content={template.description || 'No description available'}
                placement="right"
                className="w-full"
              >
                <div className="w-full">
                  <Typography 
                    variant="admin_body" 
                    className="font-medium text-admin_text"
                  >
                    {template.name}
                  </Typography>
                </div>
              </Tooltip>
            )}
            onValueItemRender={(template) => (
              <div>
                <Typography 
                  variant="admin_body" 
                  className="text-admin_text"
                >
                  {template?.name || (localValue && localValue !== "" ? localValue : 'Please select a template')}
                </Typography>
              </div>
            )}
            // Custom search function that searches name, ID, and description
            customSearchFilter={(template, searchQuery) => {
              const query = searchQuery.toLowerCase()
              const name = template.name?.toLowerCase() || ''
              const id = template.value?.toLowerCase() || ''
              const description = template.description?.toLowerCase() || ''

              return name.includes(query) || id.includes(query) || description.includes(query)
            }}
          />
        )}

        {/* Show description for selected template */}
        {selectedTemplate && selectedTemplate.description && (
          <div className="mt-3 p-4 bg-admin_paper rounded-lg border border-admin_bordered">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Typography 
                  variant="admin_body_dim" 
                  className="leading-relaxed my-0"
                >
                  {selectedTemplate.description}
                </Typography>
              </div>

              <div className="flex-shrink-0">
                <Button
                  variant="admin_ghost"
                  className="px-3 py-2 flex items-center gap-2 hover:bg-admin_neutral_2 transition-colors"
                  onClick={() => handlePreviewTemplate(selectedTemplate)}
                >
                  <EyeIcon className="w-4 h-4" />

                  <span className="text-sm font-medium">
                    Preview
                  </span>
                </Button>
              </div>
            </div>
          </div>
        )}

        {!loading && templates.length === 0 && !error && (
          <Typography variant="admin_body_dim">
            No system mail templates found. Create system templates in the Mail Templates section first.
          </Typography>
        )}
      </div>
    </div>
  )
}

export default StoreTemplateSelectorEditor